import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export const CONSENT_VERSION = 1;
export const STORAGE_KEY = 'doksan_cookie_consent';

const defaultPrefs = {
  necessary: true,
  functional: true,
  analytics: false,
  marketing: false,
};

const CookieConsentContext = createContext(null);

const createConsentId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const loadStored = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const CookieConsentProvider = ({ children }) => {
  const [consent, setConsent] = useState(null);
  const [ready, setReady] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => {
    setConsent(loadStored());
    setReady(true);
  }, []);

  const persist = useCallback((prefs, method) => {
    const record = {
      version: CONSENT_VERSION,
      ...defaultPrefs,
      ...prefs,
      necessary: true,
      functional: true,
      timestamp: new Date().toISOString(),
      consentId: createConsentId(),
      method,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
    setConsent(record);
    setSettingsOpen(false);
    return record;
  }, []);

  const acceptAll = useCallback(
    () => persist({ functional: true, analytics: true, marketing: true }, 'accept_all'),
    [persist]
  );

  const rejectAll = useCallback(
    () => persist({ functional: true, analytics: false, marketing: false }, 'reject'),
    [persist]
  );

  const saveCustom = useCallback(
    (prefs) => persist(prefs, 'custom'),
    [persist]
  );

  const allows = useCallback(
    (category) => category === 'necessary' || Boolean(consent?.[category]),
    [consent]
  );

  const value = useMemo(
    () => ({
      consent,
      ready,
      hasConsented: Boolean(consent),
      allows,
      acceptAll,
      rejectAll,
      saveCustom,
      settingsOpen,
      openSettings: () => setSettingsOpen(true),
      closeSettings: () => setSettingsOpen(false),
    }),
    [acceptAll, allows, consent, ready, rejectAll, saveCustom, settingsOpen]
  );

  return (
    <CookieConsentContext.Provider value={value}>
      {children}
    </CookieConsentContext.Provider>
  );
};

export const useCookieConsent = () => {
  const ctx = useContext(CookieConsentContext);
  if (!ctx) {
    throw new Error('useCookieConsent must be used within CookieConsentProvider');
  }
  return ctx;
};
